/**
 * Task → FullCalendar Event mapping (T2.1.2).
 *
 * Five task shapes — four renderable, one excluded:
 *   A. due_at set, all_day=false, no start/end pair
 *      → synthetic 30-min duration so the deadline has a clickable bar
 *        in timeGrid; durationEditable: false so a resize can't promote
 *        this into a real range on the backend.
 *   B. due_at set, all_day=true, no start/end pair
 *      → date-only event, end = start + 1 day (FullCalendar end is
 *        EXCLUSIVE).
 *   C. start_at + end_at set, all_day=false
 *      → straight timed range.
 *   D. start_at + end_at set, all_day=true
 *      → date-only multi-day; end = (date(end_at) + 1 day) for the
 *        exclusive bound.
 *   E. No due_at and no start/end pair — unscheduled.
 *      → ``taskToEvent`` returns ``null``; caller MUST filter null.
 *
 * Timezone: the calendar runs in Europe/Zurich (see
 * ``businessTimeZonePlugin``), and TIMED values on the wire are absolute
 * instants — ``event.start.toISOString()`` is therefore already correct
 * here and needs no conversion.
 *
 * ALL-DAY values are deliberately different: an all-day task has no time
 * of day, only a civil date, and it is stored as UTC midnight so that
 * slicing ``YYYY-MM-DD`` off the wire string yields that date in every
 * timezone. Reading a date OUT of a FullCalendar Date is the one place
 * that needs the zone, and that is what ``eventDateOnly`` is for.
 * The earlier "correct as long as the user's timezone matches the
 * storage clock" trade-off is gone.
 *
 * Editability (T2.1.4): events inherit the calendar-level
 * ``editable`` flag (set to ``!isMobile`` by the page). Shape A is the
 * one exception — it carries ``durationEditable: false`` so the
 * synthetic 30-min duration can't be resized into a real ``end_at`` on
 * the backend. ``startEditable`` is forced off via the calendar-level
 * ``eventResizableFromStart={false}``; we don't set it per-event.
 */
import { colorForAssignee, getEventColors } from "./eventStyling";
import { instantToBusinessDate } from "../../../utils/datetime";


/**
 * Classify a task's date shape. Returns one of "A" | "B" | "C" | "D" | "E".
 * Exposed for tests and the page-level visible-window filter.
 */
export function taskShape(task) {
  const hasRange = !!task?.start_at && !!task?.end_at;
  const hasDue = !!task?.due_at;
  if (hasRange) return task.all_day ? "D" : "C";
  if (hasDue) return task.all_day ? "B" : "A";
  return "E";
}


// ─── small date helpers ────────────────────────────────────────────────

function pad2(n) {
  return String(n).padStart(2, "0");
}

/** Extract "YYYY-MM-DD" from an ISO datetime string. */
function dateOnly(iso) {
  return String(iso).slice(0, 10);
}

/**
 * Add ``days`` to a "YYYY-MM-DD" date string and return the result in
 * "YYYY-MM-DD". Uses UTC arithmetic so DST transitions at the day
 * boundary don't shift the date.
 */
function addDaysDateOnly(yyyyMmDd, days) {
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return (
    `${dt.getUTCFullYear()}-` +
    `${pad2(dt.getUTCMonth() + 1)}-` +
    `${pad2(dt.getUTCDate())}`
  );
}

/** Add ``minutes`` to an ISO datetime; returns an ISO string. */
function addMinutesIso(iso, minutes) {
  const dt = new Date(iso);
  return new Date(dt.getTime() + minutes * 60 * 1000).toISOString();
}

// ─── public mapper ─────────────────────────────────────────────────────

/**
 * Map a Task (TaskListItem wire shape) into a FullCalendar EventInput.
 * Returns ``null`` for unscheduled tasks (shape E) so the caller can
 * ``.filter(Boolean)`` them out.
 */
export function taskToEvent(task, assigneeColorMap) {
  if (!task) return null;
  const shape = taskShape(task);
  if (shape === "E") return null;

  // Kalender P2a: event fill/border = assignee colour. classNames still
  // carry the status/priority markers (fc-task-status-* / fc-task-priority-*)
  // so the completed/cancelled CSS treatment keeps working; textColor is
  // intentionally NOT set so events inherit P1's --fc-event-text-color
  // (readable in both light + dark, esp. month-view dot titles).
  const colors = getEventColors(task);
  const assigneeColor = colorForAssignee(task.assigned_to_user_id, assigneeColorMap);

  const common = {
    id: task.id,
    title: task.title,
    backgroundColor: assigneeColor,
    borderColor: assigneeColor,
    classNames: colors.classNames,
    // CAL-WEEK1: `task` stays exactly as it was — `buildPatchFromEvent`
    // and the click handler read it and must not change. The two flat
    // keys are additive, and they exist so the pill can name the person
    // without knowing what kind of entity it is holding: the report
    // mapper has always emitted these same two names, so
    // `personNameFor` is one lookup for both instead of a type switch.
    extendedProps: {
      task,
      assignedToFullName: task.assigned_to_full_name || null,
      assignedToEmail: task.assigned_to_email || null,
    },
  };

  if (shape === "A") {
    const startIso = task.due_at;
    return {
      ...common,
      start: startIso,
      end: addMinutesIso(startIso, 30),
      allDay: false,
      // Synthetic 30-min duration — resizing would silently promote
      // this point-in-time deadline into a real timed range on the
      // backend. Disable per-event; the page handler also rejects
      // resize on shape A as belt-and-suspenders.
      durationEditable: false,
    };
  }
  if (shape === "B") {
    const datePart = dateOnly(task.due_at);
    return {
      ...common,
      start: datePart,
      end: addDaysDateOnly(datePart, 1),
      allDay: true,
    };
  }
  if (shape === "C") {
    return {
      ...common,
      start: task.start_at,
      end: task.end_at,
      allDay: false,
    };
  }
  // shape "D" — multi-day all-day range
  const endDate = dateOnly(task.end_at);
  return {
    ...common,
    start: dateOnly(task.start_at),
    end: addDaysDateOnly(endDate, 1),
    allDay: true,
  };
}


// ─── T2.1.4: drop / resize → backend PATCH builder ─────────────────────

/**
 * Convert a "YYYY-MM-DD" date-string to the modal/wire format used by
 * the backend for an all-day timestamp: "YYYY-MM-DDT00:00".
 * Centralised so future tz handling has one entry point.
 */
function midnightIsoLocal(yyyyMmDd) {
  return `${yyyyMmDd}T00:00`;
}

/** A FullCalendar Date → the "YYYY-MM-DD" it sits on in SWISS civil time.
 *
 *  This used to slice the date off ``d.toISOString()``, i.e. read the
 *  UTC day. FullCalendar hands back an all-day boundary as midnight in
 *  the CALENDAR's zone, and midnight in Zurich is 22:00 or 23:00 UTC the
 *  day before — so the UTC slice named the previous day and a dragged
 *  all-day task landed one day early. The unit tests never caught it
 *  because their fixtures build Dates from ``...T00:00:00Z``, which is
 *  UTC midnight, the one input for which both readings agree. */
function eventDateOnly(d) {
  return instantToBusinessDate(d);
}

/**
 * Build the MINIMAL backend PATCH payload for a FullCalendar
 * drop or resize interaction.
 *
 * @param {object} event           — FullCalendar EventApi AFTER interaction
 * @param {object} oldEvent        — FullCalendar EventApi BEFORE interaction
 * @param {"drop"|"resize"} kind   — which interaction fired
 * @returns {object|null}          — partial TaskUpdate payload, or null
 *                                   to reject the interaction (caller
 *                                   MUST then call ``info.revert()``).
 *
 * Sends only the fields semantically required for the shape transition.
 * Never resends ``title``, ``description``, ``priority``, ``task_type``,
 * ``assigned_to_user_id`` or any other untouched metadata.
 *
 * Reject conditions (returns null):
 *   - missing extendedProps.task (defensive)
 *   - shape-A resize (synthetic 30-min duration must not become a range)
 *
 * Cross-mode rules (allDay flipped during the interaction):
 *   A timed → all-day  → convert to B
 *   B all-day → timed  → convert to A
 *   C timed → all-day  → convert to D
 *   D all-day → timed  → convert to C
 *
 * Shape-B resize (single-day → multi-day) converts B → D.
 *
 * All-day end dates are "re-closed" before persistence: FullCalendar's
 * end is EXCLUSIVE, so a 3-day span ending Wed renders with
 * ``event.end = Thu``. The backend stores the inclusive end, so we
 * subtract one day before writing.
 */
export function buildPatchFromEvent(event, oldEvent, kind) {
  const task = event?.extendedProps?.task;
  if (!task) return null;

  const origShape = taskShape(task);
  if (origShape === "E") return null; // can't happen on a rendered event
  if (origShape === "A" && kind === "resize") return null;

  const wasAllDay = !!oldEvent?.allDay;
  const nowAllDay = !!event?.allDay;
  const crossMode = wasAllDay !== nowAllDay;

  // ─── DROP path ──────────────────────────────────────────────────
  if (kind === "drop") {
    if (origShape === "A") {
      if (!crossMode) {
        return { due_at: event.start.toISOString() };
      }
      // A → B (timed → all-day)
      return {
        due_at: midnightIsoLocal(eventDateOnly(event.start)),
        all_day: true,
        start_at: null,
        end_at: null,
      };
    }
    if (origShape === "B") {
      if (!crossMode) {
        return {
          due_at: midnightIsoLocal(eventDateOnly(event.start)),
          all_day: true,
        };
      }
      // B → A (all-day → timed)
      return {
        due_at: event.start.toISOString(),
        all_day: false,
        start_at: null,
        end_at: null,
      };
    }
    if (origShape === "C") {
      if (!crossMode) {
        return {
          start_at: event.start.toISOString(),
          end_at: event.end.toISOString(),
        };
      }
      // C → D (timed → all-day)
      return {
        all_day: true,
        start_at: midnightIsoLocal(eventDateOnly(event.start)),
        end_at: midnightIsoLocal(
          addDaysDateOnly(eventDateOnly(event.end), -1)
        ),
      };
    }
    // origShape === "D"
    if (!crossMode) {
      return {
        start_at: midnightIsoLocal(eventDateOnly(event.start)),
        end_at: midnightIsoLocal(
          addDaysDateOnly(eventDateOnly(event.end), -1)
        ),
      };
    }
    // D → C (all-day → timed)
    return {
      all_day: false,
      start_at: event.start.toISOString(),
      end_at: event.end.toISOString(),
    };
  }

  // ─── RESIZE path (kind === "resize") ────────────────────────────
  // Shape A already rejected above.
  if (origShape === "B") {
    // Single-day all-day → multi-day all-day → becomes shape D.
    return {
      all_day: true,
      start_at: midnightIsoLocal(eventDateOnly(event.start)),
      end_at: midnightIsoLocal(
        addDaysDateOnly(eventDateOnly(event.end), -1)
      ),
    };
  }
  if (origShape === "C") {
    // Timed-range resize: only end shifts (eventResizableFromStart=false).
    // We send both for symmetry — start_at is unchanged so the patch is
    // still semantically minimal.
    return {
      start_at: event.start.toISOString(),
      end_at: event.end.toISOString(),
    };
  }
  // origShape === "D" — all-day range resize.
  return {
    start_at: midnightIsoLocal(eventDateOnly(event.start)),
    end_at: midnightIsoLocal(
      addDaysDateOnly(eventDateOnly(event.end), -1)
    ),
  };
}
